import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tag } from 'antd';
import AddressesEditModal from './AddressesEditModal';
import Loader from '../../../../../../component/Loader/Loader';
import NoxTable from '../../../../../../component/Table';
import InnerHeader from '../../../../../../component/InnerHeader';
import AddressesAddModal from '../AddressesAddModal';

const { uuid } = require('uuidv4');

const Addresses = (props) => {
  const { addresses, customerProfileDraft, setDraftProfile, onUpdate } = props;
  const currentProject = useSelector(({ project }) => project.currentProject);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [selectedAddressId, setSelectedAddressId] = useState({});
  const [open, setOpen] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const mapToTable = (addresses) => {
    const data = [];

    if (!addresses || !addresses.length) {
      return data;
    }

    addresses.forEach((address, index) => {
      if (!address) return;
      const {
        // eslint-disable-next-line camelcase
        phone_number,
        city,
        name,
        // eslint-disable-next-line camelcase
        additional_comment,
        street,
        apartment,
        coords,
        _id,
        country
      } = address;
      const phone = `${phone_number?.country_code} ${phone_number?.number}`;
      data.push({
        apartment,
        city,
        comment: additional_comment,
        country,
        id: _id,
        key: index,
        lat: coords?.lat || 0,
        lng: coords?.lng || 0,
        name,
        phone,
        phone_number,
        street
      });
    });

    return data;
  };

  const data = useMemo(() => mapToTable(addresses), [addresses]);

  const openDetail = (item, id) => {
    return () => {
      setSelectedAddress({ ...addresses[id] });
      setOpen(true);
    };
  };

  const onClose = () => {
    setSelectedAddressId('');
    setSelectedAddress({});
    setOpen(false);
    setOpenCreateModal(false);
  };

  const onAddressAdd = () => {
    setSelectedAddress({
      _id: uuid(),
      additional_comment: '',
      apartment: '',
      city: '',
      coords: {
        lat: '0',
        lng: '0'
      },
      name: '',
      phone_number: {
        country_code: '',
        number: '',
        selected_country: 'GE'
      },
      street: ''
    });
    setOpenCreateModal(true);
  };

  const onAddressDelete = () => {
    if (!selectedRowKeys) return;
    selectedRowKeys.forEach((index) => {
      addresses.splice(index, 1);
      setDraftProfile({
        ...customerProfileDraft,
        settings: {
          ...customerProfileDraft.settings,
          addresses
        }
      });
    });
    onUpdate();
  };

  const onAddressAddSave = () => {
    setOpenCreateModal(false);
    onUpdate({ ...selectedAddress, additional_comment: selectedAddress.comment });
  };

  const onAddressUpdate = () => {
    setOpen(false);
    setSelectedAddress({});
    const newAddresses = [...addresses].map((address) => {
      if (address._id === selectedAddress._id) {
        return { ...selectedAddress, additional_comment: selectedAddress.comment };
      }
      return address;
    });
    setDraftProfile({
      ...customerProfileDraft,
      settings: { ...customerProfileDraft.settings, addresses: newAddresses }
    });

    onUpdate(newAddresses);
  };

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            {name}
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
      title: 'Name'
    },
    {
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => a.city.toString().localeCompare(b.city),
      title: 'City'
    },
    {
      dataIndex: 'street',
      key: 'street',
      sorter: (a, b) => a.street.toString().localeCompare(b.street),
      title: 'Street'
    },
    {
      dataIndex: 'apartment',
      key: 'apartment',
      sorter: (a, b) => a.apartment.toString().localeCompare(b.apartment),
      title: 'Apartment / Floor'
    },
    {
      dataIndex: 'phone',
      key: 'phone',
      sorter: (a, b) => a.phone.toString().localeCompare(b.phone),
      title: 'Phone Number'
    },
    {
      dataIndex: 'comment',
      key: 'comment',
      sorter: (a, b) => a.comment.toString().localeCompare(b.comment),
      title: 'Comment'
    },
    {
      key: 'edit',
      render: () => {
        return <Tag>edit</Tag>;
      }
    }
  ];

  const actions = [
    {
      onClick: onAddressAdd,
      title: 'Add',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift({
      onClick: onAddressDelete,
      title: 'Delete'
    });
  }

  const createModal = useCallback(() => {
    if (!selectedAddress || !Object.keys(selectedAddress)) {
      return;
    }

    return (
      <AddressesAddModal
        address={selectedAddress}
        customerProfileDraft={customerProfileDraft}
        isCreation
        onClose={onClose}
        onUpdate={onAddressAddSave}
        open={openCreateModal}
        setDraftProfile={setDraftProfile}
        setOpen={setOpenCreateModal}
        setSelectedAddress={setSelectedAddress}
      />
    );
  }, [selectedAddress]);

  if (!currentProject) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        marginTop: '80px',
        overflowY: 'auto'
      }}
    >
      <InnerHeader subtitle={data.length} title="Addresses" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={data}
        onRowClick={openDetail}
        searchBy="name"
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <AddressesEditModal
        address={selectedAddress}
        customerProfileDraft={customerProfileDraft}
        id={selectedAddressId}
        onClose={onClose}
        onUpdate={onAddressUpdate}
        open={open}
        setOpen={setOpen}
        setSelectedAddress={setSelectedAddress}
      />
      {createModal()}
    </div>
  );
};

export default Addresses;
